import { isWsMessage } from "../shared/sync";
import type { WsMessage } from "../shared/types";

export type LiveStatus = "live" | "reconnect";

export function connectLive(opts: {
  eventId: string;
  onMessage: (msg: WsMessage) => void;
  onStatus: (status: LiveStatus) => void;
  onNotFound: () => void;
  refetch: () => Promise<void>;
}): { stop: () => void } {
  let socket: WebSocket | null = null;
  let stopped = false;
  let attempt = 0;
  let fails = 0;
  let pingTimer: ReturnType<typeof setInterval> | null = null;
  let pongTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let lastPong = 0;

  function clearTimers(): void {
    if (pingTimer) clearInterval(pingTimer);
    if (pongTimer) clearTimeout(pongTimer);
    if (reconnectTimer) clearTimeout(reconnectTimer);
    pingTimer = null;
    pongTimer = null;
    reconnectTimer = null;
  }

  function open(): void {
    if (stopped) return;
    clearTimers();
    const prev = socket;
    socket = null;
    if (prev) {
      try {
        prev.close();
      } catch {
        /* ignore */
      }
    }
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const url = `${proto}://${location.host}/api/events/${opts.eventId}/ws`;
    let mine: WebSocket;
    try {
      mine = new WebSocket(url);
    } catch {
      schedule();
      return;
    }
    socket = mine;
    mine.addEventListener("open", () => {
      if (socket !== mine || stopped) return;
      attempt = 0;
      fails = 0;
      lastPong = Date.now();
      opts.onStatus("live");
      pingTimer = setInterval(() => {
        if (socket !== mine || mine.readyState !== WebSocket.OPEN) return;
        mine.send("ping");
        pongTimer = setTimeout(() => {
          if (socket !== mine) return;
          if (Date.now() - lastPong > 10_000) {
            try {
              mine.close();
            } catch {
              /* ignore */
            }
          }
        }, 10_000);
      }, 25_000);
    });
    mine.addEventListener("message", (ev) => {
      if (socket !== mine || stopped) return;
      const data = typeof ev.data === "string" ? ev.data : "";
      if (data === "pong") {
        lastPong = Date.now();
        if (pongTimer) clearTimeout(pongTimer);
        pongTimer = null;
        return;
      }
      let msg: unknown;
      try {
        msg = JSON.parse(data) as unknown;
      } catch {
        return;
      }
      if (!isWsMessage(msg)) {
        console.warn("ignored websocket frame", msg);
        return;
      }
      try {
        opts.onMessage(msg);
      } catch (err) {
        console.error(err);
        void opts.refetch().catch((e) => {
          console.error(e);
        });
      }
    });
    mine.addEventListener("close", () => {
      if (socket !== mine || stopped) return;
      opts.onStatus("reconnect");
      fails += 1;
      if (fails >= 3) {
        fails = 0;
        void checkGone();
        return;
      }
      schedule();
    });
    mine.addEventListener("error", () => {
      if (socket !== mine) return;
      try {
        mine.close();
      } catch {
        /* ignore */
      }
    });
  }

  async function checkGone(): Promise<void> {
    if (stopped) return;
    try {
      const res = await fetch(`/api/events/${opts.eventId}`);
      if (res.status === 404) {
        stopped = true;
        clearTimers();
        opts.onNotFound();
        return;
      }
    } catch {
      /* keep trying */
    }
    if (!stopped) schedule();
  }

  function schedule(): void {
    if (stopped) return;
    opts.onStatus("reconnect");
    const cap = 30_000;
    const base = Math.min(cap, 1000 * 2 ** Math.min(attempt, 8));
    const jitter = Math.floor(Math.random() * 400);
    attempt += 1;
    reconnectTimer = setTimeout(open, Math.min(cap, base + jitter));
  }

  function onVis(): void {
    if (document.visibilityState !== "visible") return;
    if (stopped) return;
    const state = socket?.readyState;
    if (state === WebSocket.CONNECTING) return;
    if (state !== WebSocket.OPEN) {
      open();
      void opts.refetch().catch((e) => {
        console.error(e);
      });
    }
  }

  document.addEventListener("visibilitychange", onVis);
  open();

  return {
    stop() {
      stopped = true;
      document.removeEventListener("visibilitychange", onVis);
      clearTimers();
      try {
        socket?.close();
      } catch {
        /* ignore */
      }
      socket = null;
    },
  };
}
